const languageEnum = {
    KO: 'ko',
    ZH: 'zh',
    EN: 'en',
}

export const commonEnum = {
    deptType: {
        DENT: 'DENT',
        OPH: 'OPH',
    },
    // 카카오톡은 한국 제조사 admin에서 복사되며 딸려온 레거시라 제거했다(2026-08-13).
    // 중국 제조사에서 쓰이는 전송 수단이 아니다.
    sendChannel: {
        WECHAT: 'WECHAT',
        EMAIL: 'EMAIL',
    },
    language: languageEnum,
    channelConfig: {
        WECHAT: {
            icon: 'fa-brands fa-weixin',
            style: { color: '#7BB32E' },
            translationKey: 'dent_issue_certificate_card_container_language_sendtype_wechat'
        },
        EMAIL: {
            icon: 'fa-solid fa-envelope',
            style: { color: '#0066CC' },
            translationKey: 'dent_issue_certificate_card_container_language_sendtype_email'
        }
    },
    // 국가번호 — 단일 원천.
    // [HARD] 라벨(국가명)을 여기에 직접 두지 않는다. i18n 키만 보유해 화면 언어를 따라간다.
    //   화면 표시는 `${value} (${$t(nameKey)})` 형태로 조립한다(countryCodeLabel 헬퍼).
    // [HARD] 선택지를 사용 통계로 줄이지 않는다 — 위챗 사용권이 본토(+86)에 한정되지 않고
    //   홍콩(+852)·마카오(+853)·대만(+886)·싱가포르(+65)로 이어지기 때문이다.
    //   2026-08-13 이전에는 이 세 곳이 전체 목록에도 없어 아예 입력이 불가능했다.
    countryCodes: [
        { value: '+86', nameKey: 'country_cn' },
        { value: '+852', nameKey: 'country_hk' },
        { value: '+853', nameKey: 'country_mo' },
        { value: '+886', nameKey: 'country_tw' },
        { value: '+65', nameKey: 'country_sg' },
        { value: '+82', nameKey: 'country_kr' },
        { value: '+81', nameKey: 'country_jp' },
        { value: '+1', nameKey: 'country_us' },
        { value: '+7', nameKey: 'country_ru' },
        { value: '+20', nameKey: 'country_eg' },
        { value: '+27', nameKey: 'country_za' },
        { value: '+30', nameKey: 'country_gr' },
        { value: '+31', nameKey: 'country_nl' },
        { value: '+32', nameKey: 'country_be' },
        { value: '+33', nameKey: 'country_fr' },
        { value: '+34', nameKey: 'country_es' },
        { value: '+36', nameKey: 'country_hu' },
        { value: '+39', nameKey: 'country_it' },
        { value: '+40', nameKey: 'country_ro' },
        { value: '+41', nameKey: 'country_ch' },
        { value: '+43', nameKey: 'country_at' },
        { value: '+44', nameKey: 'country_gb' },
        { value: '+45', nameKey: 'country_dk' },
        { value: '+46', nameKey: 'country_se' },
        { value: '+47', nameKey: 'country_no' },
        { value: '+48', nameKey: 'country_pl' },
        { value: '+49', nameKey: 'country_de' },
        { value: '+51', nameKey: 'country_pe' },
        { value: '+52', nameKey: 'country_mx' },
        { value: '+53', nameKey: 'country_cu' },
        { value: '+54', nameKey: 'country_ar' },
        { value: '+55', nameKey: 'country_br' },
        { value: '+56', nameKey: 'country_cl' },
        { value: '+57', nameKey: 'country_co' },
        { value: '+58', nameKey: 'country_ve' },
        { value: '+60', nameKey: 'country_my' },
        { value: '+61', nameKey: 'country_au' },
        { value: '+62', nameKey: 'country_id' },
        { value: '+63', nameKey: 'country_ph' },
        { value: '+64', nameKey: 'country_nz' },
        { value: '+66', nameKey: 'country_th' },
        { value: '+84', nameKey: 'country_vn' },
        { value: '+90', nameKey: 'country_tr' },
        { value: '+91', nameKey: 'country_in' },
        { value: '+92', nameKey: 'country_pk' },
        { value: '+93', nameKey: 'country_af' },
        { value: '+94', nameKey: 'country_lk' },
        { value: '+95', nameKey: 'country_mm' },
        { value: '+98', nameKey: 'country_ir' },
    ],
    // 연락처 셀렉트에 바로 노출할 국가번호(위챗 사용권 + 주요 거래국).
    // 나머지는 '직접입력'으로 여는 CountryCodeModal에서 전체 목록으로 고른다.
    primaryCountryCodeValues: ['+86', '+852', '+853', '+886', '+65', '+82', '+1'],
}

/**
 * 국가번호 표시 문자열 조립 — "+86 (中国)" 형태.
 * 번역 함수를 주입받아 호출 시점의 locale로 렌더한다(모듈 로드 시점 스냅샷 방지).
 *
 * @param {{value: string, nameKey: string}} country - commonEnum.countryCodes 항목
 * @param {(key: string) => string} t - 컴포넌트의 $t 또는 i18n.global.t
 * @returns {string}
 */
export const countryCodeLabel = (country, t) => `${country.value} (${t(country.nameKey)})`;

/**
 * 연락처 셀렉트용 목록 — primaryCountryCodeValues 순서대로 { value, label } 반환.
 * @param {(key: string) => string} t
 */
export const buildPrimaryCountryCodeList = (t) =>
    commonEnum.primaryCountryCodeValues
        .map((v) => commonEnum.countryCodes.find((c) => c.value === v))
        .filter(Boolean)
        .map((c) => ({ value: c.value, label: countryCodeLabel(c, t) }));

/**
 * 전체 국가 목록 — 모달 검색용 { value, label }.
 * @param {(key: string) => string} t
 */
export const buildFullCountryCodeList = (t) =>
    commonEnum.countryCodes.map((c) => ({ value: c.value, label: countryCodeLabel(c, t) }));
